import { useState } from "react";
import { Button, ButtonVariant, Spinner, Text } from "@merge-api/merge-javascript-shared";
import { Plus } from "lucide-react";
import UnpublishedBlueprintEmptyState from "./UnpublishedBlueprintEmptyState";
import { useStagedComponentContext } from "../../context/StagedComponentContext";
import CommonModelDiffRunModal from "./CommonModelDiffRunModal";
import useCreateCommonModelDiffRun from "./hooks/useCreateCommonModelDiffRun";
import { DiffRunFormData, ViewExecutionLogsHandler } from "./entities";
import { useGetCommonModelDiffRuns } from "./hooks/useGetCommonModelDiffRuns";
import CommonModelDiffRunResultsList from "./CommonModelDiffRunResultsList";

interface CommonModelDiffRunTabProps {
  addDiffRunDisabled?: boolean;
  blueprintCategory?: string | null;
  blueprintId?: string | null;
  blueprintVersionId?: string;
  candidateBlueprintVersionId?: string;
  candidateTitle?: string;
  className?: string;
  hasPublishedBlueprint?: boolean;
  hideAddDiffRunButton?: boolean;
  integrationID?: string;
  isAddDiffRunModalOpen?: boolean;
  onAddDiffRunModalClose?: () => void;
  showEmptyState?: boolean;
  onViewExecutionLogs?: ViewExecutionLogsHandler;
}

const CommonModelDiffRunTab = ({
  addDiffRunDisabled = false,
  blueprintCategory,
  blueprintId,
  blueprintVersionId,
  candidateBlueprintVersionId,
  candidateTitle = "Staged",
  className = "pt-6 pb-5",
  hasPublishedBlueprint,
  hideAddDiffRunButton = false,
  integrationID,
  isAddDiffRunModalOpen,
  onAddDiffRunModalClose,
  showEmptyState = false,
  onViewExecutionLogs,
}: CommonModelDiffRunTabProps) => {
  // state
  const stagedComponentContext = useStagedComponentContext();
  const effectiveBlueprintId = blueprintId ?? stagedComponentContext.component.component_id ?? null;
  const effectiveBlueprintVersionId =
    blueprintVersionId ?? stagedComponentContext.component.component_version_id ?? "";
  const effectiveBlueprintCategory =
    blueprintCategory ?? stagedComponentContext.blueprintCategory ?? null;
  const effectiveHasPublishedBlueprint =
    hasPublishedBlueprint ?? stagedComponentContext.hasPublishedBlueprint;
  const effectiveIntegrationID = integrationID ?? stagedComponentContext.integrationID;
  const [internalIsModalOpen, setInternalIsModalOpen] = useState(false);
  const isModalOpen = isAddDiffRunModalOpen ?? internalIsModalOpen;

  // handlers
  const handleCloseModal = () => {
    if (onAddDiffRunModalClose) {
      onAddDiffRunModalClose();
    } else {
      setInternalIsModalOpen(false);
    }
  };
  const handleOpenModal = () => {
    setInternalIsModalOpen(true);
  };

  // hooks
  const {
    isInitialLoad,
    commonModelDiffRunExecutions,
    setCommonModelDiffRunExecutions,
    fetchData,
  } = useGetCommonModelDiffRuns({
    blueprintVersionID: effectiveBlueprintVersionId,
  });

  const { createDiffRun } = useCreateCommonModelDiffRun({
    blueprintId: effectiveBlueprintId,
    candidateBlueprintVersionId,
    setCommonModelDiffRunExecutions,
    onSuccess: handleCloseModal,
    refreshDiffRuns: fetchData,
  });

  // form submission
  const handleAddDiffRun = (formData: DiffRunFormData) => {
    createDiffRun({
      linkedAccountId: formData.linkedAccountId,
      disableFilterByDate: formData.disableFilterByDate,
      maxPageIterations: formData.maxPageIterations,
      maxLoopIterations: formData.maxLoopIterations,
      overrideLastRunAt: formData.overrideLastRunAt,
      respectLinkedAccountScopes: formData.respectLinkedAccountScopes,
    });
  };
  const addDiffRunButton = (
    <Button
      leftIcon={<Plus size={16} />}
      disabled={addDiffRunDisabled}
      onClick={handleOpenModal}
      size="md"
      variant={ButtonVariant.TextBlack}
    >
      Add Common Model diff run
    </Button>
  );

  return (
    <div className={className}>
      {effectiveHasPublishedBlueprint ? (
        <>
          <div className="space-y-4">
            {isInitialLoad ? (
              <div className="flex justify-center items-center h-full">
                <Spinner />
              </div>
            ) : (
              <>
                {commonModelDiffRunExecutions && commonModelDiffRunExecutions.length > 0 && (
                  <CommonModelDiffRunResultsList
                    candidateTitle={candidateTitle}
                    diffRuns={commonModelDiffRunExecutions}
                    integrationID={effectiveIntegrationID}
                    setCommonModelDiffRunExecutions={setCommonModelDiffRunExecutions}
                    onViewExecutionLogs={onViewExecutionLogs}
                  />
                )}
                {showEmptyState &&
                  commonModelDiffRunExecutions &&
                  commonModelDiffRunExecutions.length === 0 && (
                    <Text
                      as="div"
                      className="flex min-h-[calc(100vh-160px)] w-full items-center justify-center text-center text-gray-40"
                      variant="md"
                    >
                      No Common Model diff runs yet.
                    </Text>
                  )}
                {!hideAddDiffRunButton && addDiffRunButton}
              </>
            )}
          </div>
          <CommonModelDiffRunModal
            blueprintCategory={effectiveBlueprintCategory}
            integrationID={effectiveIntegrationID}
            open={isModalOpen}
            onClose={handleCloseModal}
            onConfirm={handleAddDiffRun}
          />
        </>
      ) : (
        <UnpublishedBlueprintEmptyState />
      )}
    </div>
  );
};

export default CommonModelDiffRunTab;
